import { useEffect, useState } from "react";
import AgentSidebar from "../components/AgentSidebar";

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const emptyForm = {
  country: "",
  whyChooseHeadline: "",
  whyChoosePoints: [""],
  benefitsHeadline: "",
  benefitsPoints: [""],
  livingCost: [{ key: "", value: "" }],
  touristPlacesHeadline: "",
  touristPlaces: [{ name: "", description: "", imageUrl: "" }],
  isPublished: true,
};

export default function AdminCountryGuidesPage() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [heroFile, setHeroFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPlaceIndex, setUploadingPlaceIndex] = useState(null);

  const token = localStorage.getItem("token");

  const fetchGuides = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/country-guides`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => setGuides(json.success ? json.data : []))
      .finally(() => setLoading(false));
  };

  useEffect(fetchGuides, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setHeroFile(null);
    setModalOpen(true);
  };

  const openEdit = (guide) => {
    setEditingId(guide.id);
    setForm({
      country: guide.country,
      whyChooseHeadline: guide.whyChooseHeadline || "",
      whyChoosePoints: guide.whyChoosePoints?.length
        ? guide.whyChoosePoints
        : [""],
      benefitsHeadline: guide.benefitsHeadline || "",
      benefitsPoints: guide.benefitsPoints?.length
        ? guide.benefitsPoints
        : [""],
      livingCost: guide.livingCostSummary
        ? Object.entries(guide.livingCostSummary).map(([key, value]) => ({
            key,
            value,
          }))
        : [{ key: "", value: "" }],
      touristPlacesHeadline: guide.touristPlacesHeadline || "",
      touristPlaces: guide.touristPlaces?.length
        ? guide.touristPlaces
        : [{ name: "", description: "", imageUrl: "" }],
      isPublished: guide.isPublished,
    });
    setHeroFile(null);
    setModalOpen(true);
  };

  const updateListItem = (field, index, value) => {
    setForm((f) => {
      const list = [...f[field]];
      list[index] = value;
      return { ...f, [field]: list };
    });
  };
  const addListItem = (field) =>
    setForm((f) => ({ ...f, [field]: [...f[field], ""] }));
  const removeListItem = (field, index) =>
    setForm((f) => ({ ...f, [field]: f[field].filter((_, i) => i !== index) }));

  const updateLivingCost = (index, key, value) => {
    setForm((f) => {
      const list = [...f.livingCost];
      list[index] = { key, value };
      return { ...f, livingCost: list };
    });
  };
  const addLivingCostRow = () =>
    setForm((f) => ({
      ...f,
      livingCost: [...f.livingCost, { key: "", value: "" }],
    }));
  const removeLivingCostRow = (index) =>
    setForm((f) => ({
      ...f,
      livingCost: f.livingCost.filter((_, i) => i !== index),
    }));

  const updateTouristPlace = (index, field, value) => {
    setForm((f) => {
      const list = [...f.touristPlaces];
      list[index] = { ...list[index], [field]: value };
      return { ...f, touristPlaces: list };
    });
  };
  const addTouristPlace = () =>
    setForm((f) => ({
      ...f,
      touristPlaces: [
        ...f.touristPlaces,
        { name: "", description: "", imageUrl: "" },
      ],
    }));
  const removeTouristPlace = (index) =>
    setForm((f) => ({
      ...f,
      touristPlaces: f.touristPlaces.filter((_, i) => i !== index),
    }));

  const handlePlaceImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingPlaceIndex(index);
    const fd = new FormData();
    fd.append("image", file);
    const res = await fetch(`${API_BASE}/upload/image`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const json = await res.json();
    setUploadingPlaceIndex(null);
    if (json.success) updateTouristPlace(index, "imageUrl", json.data.url);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const livingCostSummary = {};
    form.livingCost.forEach(({ key, value }) => {
      if (key) livingCostSummary[key] = value;
    });

    const touristPlaces = form.touristPlaces.filter((p) => p.name);

    const fd = new FormData();
    fd.append("country", form.country);
    fd.append("whyChooseHeadline", form.whyChooseHeadline);
    fd.append(
      "whyChoosePoints",
      JSON.stringify(form.whyChoosePoints.filter(Boolean)),
    );
    fd.append("benefitsHeadline", form.benefitsHeadline);
    fd.append(
      "benefitsPoints",
      JSON.stringify(form.benefitsPoints.filter(Boolean)),
    );
    fd.append("livingCostSummary", JSON.stringify(livingCostSummary));
    fd.append("touristPlacesHeadline", form.touristPlacesHeadline);
    fd.append("touristPlaces", JSON.stringify(touristPlaces));
    fd.append("isPublished", form.isPublished);
    if (heroFile) fd.append("heroImage", heroFile);

    const url = editingId
      ? `${API_BASE}/admin/country-guides/${editingId}`
      : `${API_BASE}/admin/country-guides`;
    await fetch(url, {
      method: editingId ? "PATCH" : "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    setSaving(false);
    setModalOpen(false);
    fetchGuides();
  };

  const handleTogglePublished = async (id) => {
    await fetch(`${API_BASE}/admin/country-guides/${id}/toggle-published`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchGuides();
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        'Delete this country guide? The public "Why Choose" page for this country will stop working.',
      )
    )
      return;
    await fetch(`${API_BASE}/admin/country-guides/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchGuides();
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AgentSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <header className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-3xl font-bold text-[#00255a]">
              Country Guides
            </h2>
            <p className="text-[#64748b] mt-1">
              Manage the "Why Choose [Country]" pages.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 shadow-md hover:scale-[1.02] transition-all"
            style={{
              background:
                "linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)",
            }}
          >
            <span className="material-symbols-outlined">add</span>
            New Country Guide
          </button>
        </header>

        {loading ? (
          <p className="text-center text-[#64748b] py-20">Loading...</p>
        ) : guides.length === 0 ? (
          <p className="text-center text-[#64748b] py-20">
            No country guides yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide) => (
              <div
                key={guide.id}
                className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-hidden"
              >
                <div className="h-32 bg-[#eceef0] relative">
                  {guide.heroImageUrl && (
                    <img
                      className="w-full h-full object-cover"
                      src={guide.heroImageUrl}
                      alt={guide.country}
                    />
                  )}
                  <span
                    className={`absolute top-3 right-3 px-2 py-1 rounded text-[10px] font-bold ${guide.isPublished ? "bg-green-100 text-green-700" : "bg-[#e0e3e5] text-[#44474f]"}`}
                  >
                    {guide.isPublished ? "Published" : "Draft"}
                  </span>
                </div>
                <div className="p-5">
                  <h4 className="font-bold text-[#00255a] mb-1">
                    {guide.country}
                  </h4>
                  <p className="text-xs text-[#64748b] mb-4">
                    {guide.whyChoosePoints?.length || 0} why-choose ·{" "}
                    {guide.benefitsPoints?.length || 0} benefits ·{" "}
                    {guide.touristPlaces?.length || 0} places
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(guide)}
                      className="flex-1 py-2 border border-[#00255a] text-[#00255a] rounded-lg text-sm font-semibold hover:bg-[#00255a]/5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleTogglePublished(guide.id)}
                      className="p-2 text-[#64748b] hover:bg-[#f2f4f6] rounded-lg"
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        {guide.isPublished ? "visibility_off" : "visibility"}
                      </span>
                    </button>
                    <button
                      onClick={() => handleDelete(guide.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <span className="material-symbols-outlined text-[18px]">
                        delete
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {modalOpen && (
          <div
            className="fixed inset-0 bg-black/40 flex items-start justify-center z-[100] p-4 overflow-y-auto"
            onClick={() => setModalOpen(false)}
          >
            <form
              onSubmit={handleSave}
              className="bg-white rounded-2xl p-8 w-full max-w-2xl shadow-2xl space-y-6 my-8 mt-12"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-[#00255a]">
                {editingId ? "Edit Country Guide" : "New Country Guide"}
              </h3>

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Country
                </label>
                <input
                  required
                  disabled={!!editingId}
                  value={form.country}
                  onChange={(e) =>
                    setForm({ ...form, country: e.target.value })
                  }
                  placeholder="e.g. Turkey"
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10 disabled:bg-[#f2f4f6]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Hero Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setHeroFile(e.target.files[0])}
                  className="text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Why Choose Headline
                </label>
                <input
                  value={form.whyChooseHeadline}
                  onChange={(e) =>
                    setForm({ ...form, whyChooseHeadline: e.target.value })
                  }
                  placeholder="e.g. Why Choose Turkey?"
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
                />
              </div>

              <PointListEditor
                label="Why Choose Points"
                items={form.whyChoosePoints}
                onChange={(i, v) => updateListItem("whyChoosePoints", i, v)}
                onAdd={() => addListItem("whyChoosePoints")}
                onRemove={(i) => removeListItem("whyChoosePoints", i)}
              />

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Benefits Headline
                </label>
                <input
                  value={form.benefitsHeadline}
                  onChange={(e) =>
                    setForm({ ...form, benefitsHeadline: e.target.value })
                  }
                  placeholder="e.g. What You Gain"
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10"
                />
              </div>

              <PointListEditor
                label="Benefits Points"
                items={form.benefitsPoints}
                onChange={(i, v) => updateListItem("benefitsPoints", i, v)}
                onAdd={() => addListItem("benefitsPoints")}
                onRemove={(i) => removeListItem("benefitsPoints", i)}
              />

              <div>
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Cost of Living
                </label>
                <div className="space-y-2">
                  {form.livingCost.map((row, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        value={row.key}
                        onChange={(e) =>
                          updateLivingCost(i, e.target.value, row.value)
                        }
                        placeholder="e.g. accommodation"
                        className="flex-1 px-3 py-2 rounded-lg border border-[#e2e8f0] outline-none text-sm"
                      />
                      <input
                        value={row.value}
                        onChange={(e) =>
                          updateLivingCost(i, row.key, e.target.value)
                        }
                        placeholder="e.g. $130 - $170 USD per month"
                        className="flex-[2] px-3 py-2 rounded-lg border border-[#e2e8f0] outline-none text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeLivingCostRow(i)}
                        className="p-2 text-red-500"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          remove_circle
                        </span>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addLivingCostRow}
                    className="text-[#00255a] text-sm font-semibold flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      add
                    </span>{" "}
                    Add Row
                  </button>
                </div>
              </div>

              <div className="border-t border-[#e2e8f0] pt-6">
                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Tourist / Beautiful Places Headline
                </label>
                <input
                  value={form.touristPlacesHeadline}
                  onChange={(e) =>
                    setForm({ ...form, touristPlacesHeadline: e.target.value })
                  }
                  placeholder="e.g. Places to Explore in Turkey"
                  className="w-full px-4 py-3 rounded-lg border border-[#e2e8f0] outline-none focus:ring-2 focus:ring-[#00255a]/10 mb-4"
                />

                <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
                  Places
                </label>
                <div className="space-y-4">
                  {form.touristPlaces.map((place, i) => (
                    <div
                      key={i}
                      className="p-4 bg-[#f7f9fb] rounded-xl border border-[#e2e8f0] space-y-2"
                    >
                      <div className="flex gap-2">
                        <input
                          value={place.name}
                          onChange={(e) =>
                            updateTouristPlace(i, "name", e.target.value)
                          }
                          placeholder="Place name (e.g. Cappadocia)"
                          className="flex-1 px-3 py-2 rounded-lg border border-[#e2e8f0] outline-none text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeTouristPlace(i)}
                          className="p-2 text-red-500"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            remove_circle
                          </span>
                        </button>
                      </div>
                      <textarea
                        value={place.description}
                        onChange={(e) =>
                          updateTouristPlace(i, "description", e.target.value)
                        }
                        placeholder="Short description..."
                        rows="2"
                        className="w-full px-3 py-2 rounded-lg border border-[#e2e8f0] outline-none text-sm resize-none"
                      />
                      <div className="flex items-center gap-3">
                        {place.imageUrl && (
                          <img
                            src={place.imageUrl}
                            alt={place.name}
                            className="w-12 h-12 rounded-lg object-cover border border-[#e2e8f0]"
                          />
                        )}
                        <label className="text-sm text-[#00255a] font-semibold cursor-pointer">
                          {uploadingPlaceIndex === i
                            ? "Uploading..."
                            : place.imageUrl
                              ? "Replace Photo"
                              : "Upload Photo"}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) =>
                              handlePlaceImageUpload(i, e.target.files[0])
                            }
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTouristPlace}
                    className="text-[#00255a] text-sm font-semibold flex items-center gap-1"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      add
                    </span>{" "}
                    Add Place
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) =>
                    setForm({ ...form, isPublished: e.target.checked })
                  }
                  className="w-5 h-5"
                />
                <label className="text-sm text-[#44474f]">
                  Published (visible on public site)
                </label>
              </div>

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-3 border border-[#e2e8f0] rounded-lg font-semibold text-[#44474f]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 text-white rounded-lg font-semibold disabled:opacity-60"
                  style={{
                    background:
                      "linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)",
                  }}
                >
                  {saving
                    ? "Saving..."
                    : editingId
                      ? "Save Changes"
                      : "Create Guide"}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}

function PointListEditor({ label, items, onChange, onAdd, onRemove }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">
        {label}
      </label>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={item}
              onChange={(e) => onChange(i, e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-[#e2e8f0] outline-none text-sm"
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="p-2 text-red-500"
            >
              <span className="material-symbols-outlined text-[18px]">
                remove_circle
              </span>
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={onAdd}
          className="text-[#00255a] text-sm font-semibold flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[16px]">add</span> Add
          Point
        </button>
      </div>
    </div>
  );
}
